import { Provider } from '@vgtv/api-client';

import { config } from '../../config.js';
import { sessionStorageHelper } from '../../helpers/storage/index.js';

import {
  getPageViewOrigin,
  setCurrentPageViewData,
  getPreviousPageViewOrigin,
  getCurrentPageViewData,
} from './helpers/pulseOrigin';
import { getCurrentPageObject, getPageObject } from './helpers/pulsePage';
import { getPulseProvider } from './helpers/pulseProvider';
import { pulseTrackEvent, pulseTrackPageView } from './pulse/pulse';

import type { TrackablePageType } from './helpers/pulsePage';
import type { SessionSDRN, FlowSDRN, SDRN } from './helpers/getSDRN';
import type { Vendor } from '../../types.js';
import type { AppConfig } from '../../config.js';
import type { EventInput } from '@spt-tracking/pulse-sdk';

/**
 * Type documentation
 * @see https://pages.github.schibsted.io/mpt-data/pulse-standard
 */
export interface ActorObject {
  id?: string;
  'spt:userId'?: string;
  subscriptionName?: string;

  // Custom properties
  realm?: AppConfig['pulse']['actorRealm'];
}

export interface PulseExperiment {
  id: string;
  variant: string;
  name: string;
  // Stringified JSON
  custom?: string;

  // Custom properties
  platform: string;
}

interface PulseEventPlacement {
  primaryPosition: string;
  secondaryPosition?: string;
  source?: string;
  curateContext?: string;
  columnName?: 'main' | 'aside';
  positionInBundle?: number;
}

interface PulseEventLayout {
  elementSize: string;
  imageUrl?: string;
}

export interface PulseEventObject {
  '@id'?: SDRN;
  '@type'?: string;
  accessLevel?: 'Paid' | 'Free' | 'Meter';
  accessLevelName?: string;
  category?: string;
  contentId?: string;
  position?: string;
  custom?: Record<string, boolean | string | number>;
  'spt:custom'?: Record<string, any>;
  elementType?: string;
  id?: string;
  imageUrl?: string;
  layout?: PulseEventLayout;
  name?: string;
  navigationType?: 'navigation' | 'alternative' | 'unknown';
  newsroom?: string;
  parent?: object; // todo
  page?: PulseEventObject;
  permaLink?: string;
  previewUrl?: string;
  placement?: PulseEventPlacement;
  published?: string;
  salesPosterType?: string;
  shareUrl?: string;
  sponsor?: string;
  tags?: string[];
  type?: string;
  updated?: string;
  url?: string;
}

export interface PulseEventOrigin {
  '@id'?: SDRN;
  '@type'?: PulseEventObject['type'];
  category?: PulseEventObject['category'];
  name?: string;
  element?: string;
  url?: string;
  source?: string;
  channel?: string;
  campaign?: string;
  content?: string;
  terms?: string;
  xDomainId?: string;
}

export interface PulseEventDevice {
  deviceType?: string;
  acceptLanguague?: string;
  screenSize?: string;
  userAgent?: string;
  viewPortSize?: string;
  localStorageEnabled?: boolean;
  environmentId?: string;
  adBlocker?: boolean;
}

export interface PulseEventSession {
  '@id': SessionSDRN;
  '@type': 'Session';
  start: string;
  end: string;
  flow?: {
    '@id': FlowSDRN;
    start: string;
    end: string;
    origin?: {
      '@type'?: string;
      url?: string;
      campaign?: string;
      channel?: string;
      source?: string;
      content?: string;
      terms?: string;
    };
  };
}

export interface PulseEventTracker {
  '@type': string;
  name: string;
  type: string;
  version: string;
}

export interface PulseEvent {
  type?: string;
  action?: string;
  intent?: string;
  object?: PulseEventObject;
  origin?: PulseEventOrigin;
  target?: {
    '@type'?: PulseEvent['type'];
    '@id'?: string;
    type?: PulseEvent['type'];
    id?: string | number;
    name?: string;
    url?: string;
    category?: string;
    tags?: string[];
  };
  actor?: ActorObject | Promise<ActorObject>;
  provider?: {
    product?: string;
    productType?: string;
    'spt:engage'?: string;
    productTag?: string;
  };
  session?: PulseEventSession;
  device?: PulseEventDevice;
  tracker?: PulseEventTracker;
  experiments?: PulseExperiment[];
}

async function trackPulsePageView<T extends TrackablePageType>({
  pageType,
  entity,
  vendor,
  isSalesposter,
  callback,
}: {
  pageType: T;
  entity: any;
  vendor: Vendor;
  isSalesposter?: boolean;
  callback?: () => void;
}) {
  const isPwa = sessionStorageHelper.getItem('isPwa');

  const object: PulseEventObject = await getPageObject({
    pageType,
    vendor,
    data: entity,
    isSalesposter,
  });

  const origin = getPageViewOrigin();

  const data: PulseEvent = {
    object,
    provider: getPulseProvider(vendor),
  };

  if (config.provider === Provider.VGTV && isPwa === 'true') {
    data.provider!.productType = 'PWA';
  }

  //on AB View PaywallButton is being sent before View Salesposter causing incorrect xdomain tracking
  const xDomainId =
    config.provider === Provider.AB
      ? getCurrentPageViewData().xDomainId
      : crypto.randomUUID();

  if (origin) {
    data.origin = origin;
    if (isSalesposter) {
      data.origin.xDomainId = xDomainId;
    }
  }

  setCurrentPageViewData({
    object,
    url: window.location.href,
    timestamp: new Date().getTime(),
    xDomainId,
  });

  if (config.env === 'development' && !config.enableTracking) {
    console.log('[Pulse]: PageView', data);
  } else {
    try {
      await pulseTrackPageView(data as EventInput);
    } finally {
      callback?.();
    }
  }
}

async function rawTrackEngagementEvent(event: PulseEvent, xDomainId?: string) {
  const origin = getPreviousPageViewOrigin();

  const data: PulseEvent = {
    type: 'Engagement',
    origin: {
      ...origin,
      ...(xDomainId && { xDomainId }),
    },

    ...event,
  };

  if (data.object) {
    data.object.page = await getCurrentPageObject();
  }

  if (config.env === 'development' && !config.enableTracking) {
    console.log('[Pulse]: Event', data);
  } else {
    return pulseTrackEvent('engagementEvent', data as EventInput);
  }
}

async function rawTrackViewEvent(event: PulseEvent, xDomainId?: string) {
  const previousOrigin = getPreviousPageViewOrigin();

  const data: PulseEvent = {
    type: 'View',
    origin:
      previousOrigin || xDomainId
        ? {
            ...previousOrigin,
            ...(xDomainId && { xDomain: xDomainId }),
          }
        : undefined,
    ...event,
  };

  if (data.object) {
    data.object.page = await getCurrentPageObject();
  }

  if (config.env === 'development' && !config.enableTracking) {
    console.log('[Pulse]: Event', data);
  } else {
    return pulseTrackEvent('trackerEvent', data as EventInput);
  }
}

export {
  getPageViewOrigin,
  rawTrackEngagementEvent,
  rawTrackViewEvent,
  trackPulsePageView,
};
